import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** 画面を切り替えたときに復帰させるためのキー。ふつうは現在のパスを渡す。 */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

/**
 * 描画エラーの受け止め (SPEC 67, 103)。
 *
 * React はエラー境界が無いと、ツリー全体をアンマウントする。
 * 1 ページの不具合でヘッダーもナビゲーションも消え、真っ白な画面になる。
 * 競技中にそれが起きると、書きかけのコードへ戻る手段まで失われる。
 *
 * ここで受け止めて、少なくとも移動はできる状態を残す。
 * 例外の詳細はコンソールにのみ出し、画面には出さない
 * （内部情報を利用者へ見せない方針は API 側と揃える）。
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // 将来エラー収集基盤を入れるならここから送る (SPEC 68)。
    console.error('画面の描画に失敗しました', error, info.componentStack);
  }

  override componentDidUpdate(previous: Props): void {
    // 別のページへ移動したら復帰させる。壊れたページに閉じ込めない。
    if (this.state.error !== null && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override render(): ReactNode {
    if (this.state.error === null) {
      return this.props.children;
    }

    return (
      <div className="c-empty" role="alert">
        <h1>表示できませんでした</h1>
        <p className="u-muted">
          このページの表示中に問題が発生しました。
          ページを再読み込みするか、別のページへ移動してください。
        </p>
        <button
          type="button"
          className="c-button"
          onClick={() => this.setState({ error: null })}
        >
          再試行
        </button>
      </div>
    );
  }
}
